import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Close, Work } from '@mui/icons-material';

// Components
import { Modal, Loader } from '../../../../../components';
import {
  Fade,
  ButtonGroup,
  Button,
  Grid,
  TextField,
  Box,
  Divider,
  Card as CardMui,
  CardContent,
  CardHeader,
  Paper
} from '@mui/material';
// models
// fetch
import { useSnackbar } from 'notistack';
import { createColorPalette } from '../../../../../fetch/color-palettes';
import { useHistory } from 'react-router-dom';

interface IAddColorPaletteModal {
  open: boolean;
  onClose: () => void;
}

const AddColorPaletteSchema = Yup.object().shape({
  name: Yup.string().required('Required')
});

export const AddColorPaletteModal: FC<IAddColorPaletteModal> = ({ open, onClose }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: ''
        }}
        validationSchema={AddColorPaletteSchema}
        onSubmit={async (values, actions) => {
          const data: any = {
            ...values
          };
          try {
            const colorPaletteId = await createColorPalette(data);
            enqueueSnackbar('Color Palette added!', {
              variant: 'success'
            });

            onClose();
            history.push(
              `/development/options/option-sets-and-palettes/color-palette-sets/${colorPaletteId}`
            );
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Color Palette was not added', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                  resetForm();
                }
              }}
              maxWidth="sm"
              title={`Add Color Palette`}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <CardMui elevation={0} variant="outlined" className={classes.marginBottom}>
                        <CardHeader
                          avatar={<Work />}
                          title="INFO"
                          className={classes.primaryHeader}
                        />
                        <CardContent className={classes.cardContent}>
                          <Paper elevation={0} square>
                            <div className={classes.content}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Name"
                                    name="name"
                                    size="small"
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('name', e.target.value)}
                                    error={touched?.name && errors?.name ? true : false}
                                  />
                                </Grid>
                              </Grid>
                            </div>
                            <Box mt={1} mb={0.5}>
                              <Divider />
                            </Box>
                            <ButtonGroup>
                              <Button
                                disabled={!dirty || isSubmitting || !isValid}
                                type="submit"
                                startIcon={<Add />}
                                variant="contained"
                                color="primary"
                              >
                                {'Add'}
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="inherit"
                                startIcon={<Close />}
                                onClick={() => {
                                  if (!deepEqual(initialValues, values)) {
                                    const result = window.confirm(
                                      'You have unsaved changes, are you sure you want to exit?'
                                    );
                                    if (result) {
                                      resetForm();
                                      onClose();
                                    } else {
                                      return;
                                    }
                                  } else {
                                    onClose();
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </Paper>
                        </CardContent>
                      </CardMui>
                    </Grid>
                  </Grid>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
